/**
 * OAuth Authentication Service for ULTRON MCP
 */

import { logger } from '../utils/logger';

interface OAuthConfig {
  clientId: string;
  redirectUri: string;
  scope: string;
  authorizationUrl: string;
  tokenUrl: string;
}

interface AuthTokens {
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
  tokenType: string;
}

interface UserProfile {
  id: string;
  email: string;
  name: string;
  groups: string[];
  permissions: string[];
}

class AuthService {
  private config: OAuthConfig;
  private tokens: AuthTokens | null = null;
  private profile: UserProfile | null = null;
  private keepLoggedInCache: boolean | null = null; // In-memory cache to prevent circular dependency

  constructor() {
    this.config = {
      clientId: (process.env.REACT_APP_MCP_CLIENT_ID || '').trim(),
      redirectUri: process.env.REACT_APP_MCP_REDIRECT_URI || `${window.location.origin}/auth/callback`,
      scope: process.env.REACT_APP_MCP_SCOPE || 'read:devices read:groups read:data',
      authorizationUrl: process.env.REACT_APP_MCP_AUTH_URL || 'https://api.ultroncloud.com/oauth/authorize',
      tokenUrl: process.env.REACT_APP_MCP_TOKEN_URL || 'https://api.ultroncloud.com/oauth/token'
    };

    // Load tokens from sessionStorage
    this.loadStoredTokens();
  }

  /**
   * Check if MCP OAuth is enabled
   */
  public isOAuthEnabled(): boolean {
    const envValue = process.env.REACT_APP_MCP_OAUTH_ENABLED?.trim();
    const enabled = envValue === 'true';
    const hasClientId = !!this.config.clientId;
    return enabled && hasClientId;
  }

  /**
   * Check if user is authenticated
   * Note: Does NOT logout on expiration - let callers decide whether to refresh
   */
  public isAuthenticated(): boolean {
    if (!this.tokens) return false;

    // Check if token is expired
    const expiresAt = this.getTokenExpiration();
    if (expiresAt && new Date() > expiresAt) {
      // ✅ Don't logout here! Let caller handle refresh
      // The token might still be refreshable with refreshToken
      logger.log('⚠️ Token expired, returning false (not logging out - caller should try refresh)');
      return false;
    }

    return true;
  }

  /**
   * Initiate OAuth login flow
   */
  public login(): void {
    if (!this.isOAuthEnabled()) {
      logger.error('OAuth is not enabled or configured');
      return;
    }

    // Generate state for CSRF protection
    const state = this.generateState();
    sessionStorage.setItem('oauth_state', state);

    // Build authorization URL
    const params = new URLSearchParams({
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      response_type: 'code',
      scope: this.config.scope,
      state: state
    });

    // Redirect to authorization endpoint
    window.location.href = `${this.config.authorizationUrl}?${params.toString()}`;
  }

  /**
   * Handle OAuth callback
   */
  public async handleCallback(code: string, state: string): Promise<boolean> {
    try {
      // Verify state
      const storedState = sessionStorage.getItem('oauth_state');
      if (state !== storedState) {
        throw new Error('Invalid state parameter');
      }
      sessionStorage.removeItem('oauth_state');

      // Exchange code for tokens
      const tokens = await this.exchangeCodeForTokens(code);
      if (!tokens) {
        throw new Error('Failed to exchange code for tokens');
      }

      // Store tokens
      this.tokens = tokens;
      await this.storeTokens(tokens);

      // Fetch user profile
      await this.fetchUserProfile();

      return true;
    } catch (error) {
      logger.error('OAuth callback error:', error);
      return false;
    }
  }

  /**
   * Exchange authorization code for tokens
   */
  private async exchangeCodeForTokens(code: string): Promise<AuthTokens | null> {
    try {
      const response = await fetch(this.config.tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: this.config.clientId,
          code: code,
          redirect_uri: this.config.redirectUri
        })
      });

      if (!response.ok) {
        throw new Error(`Token exchange failed: ${response.statusText}`);
      }

      const data = await response.json();
      
      return {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresIn: data.expires_in || 3600,
        tokenType: data.token_type || 'Bearer'
      };
    } catch (error) {
      logger.error('Token exchange error:', error);
      return null;
    }
  }

  /**
   * Fetch user profile using access token
   */
  private async fetchUserProfile(): Promise<void> {
    if (!this.tokens) return;

    try {
      const response = await fetch('https://api.ultroncloud.com/api/user/profile', {
        headers: {
          'Authorization': `${this.tokens.tokenType} ${this.tokens.accessToken}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user profile');
      }

      const profile = await response.json();
      this.profile = {
        id: profile.id || profile.uid,
        email: profile.email,
        name: profile.name || profile.displayName,
        groups: profile.groups || [],
        permissions: profile.permissions || []
      };

      // Store profile
      sessionStorage.setItem('user_profile', JSON.stringify(this.profile));
    } catch (error) {
      logger.error('Profile fetch error:', error);
    }
  }

  /**
   * Get current access token
   */
  public getAccessToken(): string | null {
    return this.tokens?.accessToken || null;
  }

  /**
   * Get authorization headers
   */
  public getAuthHeaders(): Record<string, string> {
    if (!this.tokens) return {};
    
    return {
      'Authorization': `${this.tokens.tokenType} ${this.tokens.accessToken}`
    };
  }

  /**
   * Get user profile
   */
  public getUserProfile(): UserProfile | null {
    return this.profile;
  }

  /**
   * Get user's default group ID
   */
  public getDefaultGroupId(): string | null {
    if (!this.profile || !this.profile.groups.length) return null;
    return this.profile.groups[0];
  }

  /**
   * Refresh access token
   */
  public async refreshToken(): Promise<boolean> {
    if (!this.tokens?.refreshToken) return false;

    try {
      // Use Turing API endpoint for token refresh
      const appId = sessionStorage.getItem('selected_app_id') || process.env.REACT_APP_ULTRON_APP_ID || '';

      const response = await fetch('https://turing.ultronsmart.com/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          refresh_token: this.tokens.refreshToken,
          app_id: appId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { message: errorText };
        }
        const error = new Error(`Token refresh failed: ${response.status} ${errorData.message || errorText}`);
        (error as any).response = { status: response.status, data: errorData };
        throw error;
      }

      const data = await response.json();

      if (!data.success || !data.access_token) {
        const error = new Error('Invalid refresh response');
        (error as any).response = { status: 401, data };
        throw error;
      }

      this.tokens = {
        accessToken: data.access_token,
        refreshToken: this.tokens.refreshToken, // Keep existing refresh token
        expiresIn: data.expires_in || 3600,
        tokenType: 'Bearer'
      };

      // ✅ Store in dynamic storage (localStorage or sessionStorage based on keepLoggedIn)
      await this.storeTokens(this.tokens);

      // ✅ Also store with ultron_tokens key for compatibility (using same dynamic storage)
      const session = {
        accessToken: this.tokens.accessToken,
        refreshToken: this.tokens.refreshToken,
        expiresAt: Date.now() + (this.tokens.expiresIn * 1000)
      };
      const storage = await this.getStorageType();
      storage.setItem('ultron_tokens', JSON.stringify(session));

      logger.log('✅ Token refreshed successfully via Turing API');
      return true;
    } catch (error: any) {
      logger.error('Token refresh error:', error);

      // ✅ Only dispatch session-expired for genuine auth errors (401, 403)
      // Don't dispatch for network errors, timeouts, or server errors
      const isAuthError = error?.response?.status === 401 ||
                         error?.response?.status === 403 ||
                         error?.message?.includes('Invalid refresh response') ||
                         error?.message?.includes('refresh_token') ||
                         error?.message?.includes('Token refresh failed');

      if (isAuthError) {
        logger.warn('⚠️ Refresh token is invalid or expired - dispatching session-expired event');
        window.dispatchEvent(new CustomEvent('session-expired'));
      } else {
        logger.warn('⚠️ Token refresh failed due to temporary error - NOT dispatching session-expired');
        logger.warn('Error details:', {
          status: error?.response?.status,
          message: error?.message,
          isNetworkError: !error?.response
        });
      }

      return false;
    }
  }

  /**
   * Logout user
   */
  public logout(): void {
    this.tokens = null;
    this.profile = null;

    // Clear from both localStorage and sessionStorage
    sessionStorage.removeItem('auth_tokens');
    sessionStorage.removeItem('user_profile');
    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('auth_mode');
    sessionStorage.removeItem('ultron_tokens');

    localStorage.removeItem('auth_tokens');
    localStorage.removeItem('user_profile');
    localStorage.removeItem('ultron_tokens');

    // Clear API credentials (keep in localStorage - user manually entered)
    // localStorage.removeItem('ultron-energy-credentials'); // 保留，API Key 是用户手动输入的

    logger.log('🔐 Logged out, cleared tokens from both storages');

    // Redirect to login page
    window.location.href = '/login';
  }

  /**
   * Get storage type based on keepLoggedIn preference
   * Uses in-memory cache to prevent circular dependency with getUserPreferences
   * @returns localStorage if keepLoggedIn is true, sessionStorage otherwise
   */
  private async getStorageType(): Promise<Storage> {
    try {
      // ✅ Use cached value first (prevents circular dependency)
      if (this.keepLoggedInCache !== null) {
        const storage = this.keepLoggedInCache ? localStorage : sessionStorage;
        logger.log(`🔐 Storage type (cached): ${this.keepLoggedInCache ? 'localStorage' : 'sessionStorage'}`);
        return storage;
      }

      // ✅ Fallback: Check localStorage directly without calling getUserPreferences
      // This breaks the circular dependency during token refresh
      const settingsStr = localStorage.getItem('ultron-energy-settings');
      if (settingsStr) {
        try {
          const settings = JSON.parse(settingsStr);
          const keepLoggedIn = settings.keepLoggedIn || false;
          this.keepLoggedInCache = keepLoggedIn; // Cache for next time
          logger.log(`🔐 Storage type (localStorage): ${keepLoggedIn ? 'localStorage' : 'sessionStorage'} (keepLoggedIn: ${keepLoggedIn})`);
          return keepLoggedIn ? localStorage : sessionStorage;
        } catch (e) {
          logger.error('Failed to parse settings from localStorage:', e);
        }
      }

      // ✅ Default: sessionStorage (safest option)
      logger.log('🔐 Storage type (default): sessionStorage');
      return sessionStorage;
    } catch (error) {
      logger.error('Failed to get keepLoggedIn preference, defaulting to sessionStorage:', error);
      return sessionStorage;
    }
  }

  /**
   * Store tokens in dynamic storage (localStorage or sessionStorage based on keepLoggedIn)
   */
  private async storeTokens(tokens: AuthTokens): Promise<void> {
    const expiresAt = new Date();
    expiresAt.setSeconds(expiresAt.getSeconds() + tokens.expiresIn);

    const storedTokens = {
      ...tokens,
      expiresAt: expiresAt.toISOString()
    };

    // Get dynamic storage based on keepLoggedIn preference
    const storage = await this.getStorageType();
    storage.setItem('auth_tokens', JSON.stringify(storedTokens));

    logger.log(`✅ Tokens stored in ${storage === localStorage ? 'localStorage' : 'sessionStorage'}`);
  }

  /**
   * Load tokens from storage (checks both localStorage and sessionStorage)
   */
  private loadStoredTokens(): void {
    // Try localStorage first (for keepLoggedIn = true)
    let stored = localStorage.getItem('auth_tokens');
    let storageType = 'localStorage';

    // Fallback to sessionStorage
    if (!stored) {
      stored = sessionStorage.getItem('auth_tokens');
      storageType = 'sessionStorage';
    }

    if (stored) {
      try {
        this.tokens = JSON.parse(stored);
        logger.log(`🔐 Loaded tokens from ${storageType}`);

        // Also load profile from the same storage
        const profileStored = storageType === 'localStorage'
          ? localStorage.getItem('user_profile')
          : sessionStorage.getItem('user_profile');

        if (profileStored) {
          this.profile = JSON.parse(profileStored);
        }
      } catch (error) {
        logger.error('Error loading stored tokens:', error);
      }
    }
  }

  /**
   * Get token expiration time (checks both localStorage and sessionStorage)
   */
  private getTokenExpiration(): Date | null {
    // Try localStorage first
    let stored = localStorage.getItem('auth_tokens');

    // Fallback to sessionStorage
    if (!stored) {
      stored = sessionStorage.getItem('auth_tokens');
    }

    if (!stored) return null;

    try {
      const data = JSON.parse(stored);
      return data.expiresAt ? new Date(data.expiresAt) : null;
    } catch {
      return null;
    }
  }

  /**
   * Generate random state for CSRF protection
   */
  private generateState(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Initialize tokens from storage (checks both localStorage and sessionStorage)
   * Used when Turing Server provides tokens via URL hash
   */
  public async initializeFromSessionStorage(): Promise<void> {
    // Try localStorage first (for keepLoggedIn = true)
    let storedTokens = localStorage.getItem('ultron_tokens');
    let storageType = 'localStorage';

    // Fallback to sessionStorage
    if (!storedTokens) {
      storedTokens = sessionStorage.getItem('ultron_tokens');
      storageType = 'sessionStorage';
    }

    if (!storedTokens) return;

    try {
      const session = JSON.parse(storedTokens);

      // Check if tokens are expired
      if (session.expiresAt && Date.now() >= session.expiresAt) {
        logger.warn('⚠️ Stored tokens are expired, attempting refresh...');

        // Try to refresh using the stored refresh token
        if (session.refreshToken) {
          this.tokens = {
            accessToken: session.accessToken,
            refreshToken: session.refreshToken,
            expiresIn: 0, // Expired
            tokenType: 'Bearer'
          };

          // Attempt refresh
          const refreshSuccess = await this.refreshToken();
          if (refreshSuccess) {
            logger.log('✅ Expired token refreshed successfully on startup');
            this.setupTokenRefresh();
            return;
          }
        }

        // Refresh failed or no refresh token - clear and return
        logger.warn('⚠️ Token refresh failed, clearing stored tokens');
        sessionStorage.removeItem('ultron_tokens');
        localStorage.removeItem('ultron_tokens');
        return;
      }

      // Calculate expiresIn from expiresAt
      const expiresIn = session.expiresAt
        ? Math.floor((session.expiresAt - Date.now()) / 1000)
        : 3600;

      // Initialize tokens
      this.tokens = {
        accessToken: session.accessToken,
        refreshToken: session.refreshToken,
        expiresIn: expiresIn,
        tokenType: 'Bearer'
      };

      logger.secure(`Initialized tokens from ${storageType} (expires in ${Math.floor(expiresIn / 60)} minutes)`);

      // Start automatic token refresh
      this.setupTokenRefresh();
    } catch (error) {
      logger.error('❌ Error initializing tokens from storage:', error);
    }
  }

  /**
   * @deprecated Use initializeFromSessionStorage instead
   * Kept for backward compatibility
   */
  public async initializeFromLocalStorage(): Promise<void> {
    // Try to migrate from localStorage to sessionStorage
    const oldTokens = localStorage.getItem('ultron_tokens');
    if (oldTokens) {
      try {
        JSON.parse(oldTokens); // Validate JSON
        sessionStorage.setItem('ultron_tokens', oldTokens);
        localStorage.removeItem('ultron_tokens'); // Remove from localStorage
        logger.log('✅ Migrated tokens from localStorage to sessionStorage');
      } catch (e) {
        logger.error('Error migrating tokens:', e);
      }
    }
    // Use new method
    await this.initializeFromSessionStorage();
  }

  /**
   * Setup automatic token refresh
   */
  public setupTokenRefresh(): void {
    if (!this.tokens) {
      logger.warn('⚠️ Cannot setup token refresh: no tokens available');
      return;
    }

    // Calculate when to refresh (5 minutes before expiration)
    const refreshTime = Math.max((this.tokens.expiresIn - 300) * 1000, 60000); // At least 1 minute

    logger.log(`🔄 Token refresh scheduled in ${Math.floor(refreshTime / 60000)} minutes`);

    setTimeout(async () => {
      logger.log('🔄 Attempting token refresh...');
      const success = await this.refreshToken();
      if (success) {
        logger.log('✅ Token refreshed successfully');
        this.setupTokenRefresh(); // Setup next refresh
      } else {
        logger.error('❌ Token refresh failed');
      }
    }, refreshTime);
  }

  /**
   * Update the keepLoggedIn cache
   * Call this when user changes the keepLoggedIn setting
   * @param keepLoggedIn New value for keepLoggedIn setting
   */
  public updateKeepLoggedInCache(keepLoggedIn: boolean): void {
    this.keepLoggedInCache = keepLoggedIn;
    logger.log(`✅ keepLoggedIn cache updated: ${keepLoggedIn}`);
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;